import type React from "react";
import { type ReactNode } from "react";
import {
  memo,
  useMemo,
  useCallback,
  useSyncExternalStore,
  useEffect,
} from "react";

import { Transition } from "@headlessui/react";

import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import { type PageModal, type TablescapeState } from "@tablescape/state";
import { tw } from "classnames";

const transitionAnimation = {
  backdrop: {
    enter: tw("transition-opacity", "ease-linear", "duration-300"),
    enterFrom: tw("opacity-0"),
    enterTo: tw("opacity-80"),
    leave: tw("transition-opacity", "ease-linear", "duration-300"),
    leaveFrom: tw("opacity-80"),
    leaveTo: tw("opacity-0"),
  },
  modal: {
    enter: tw("transition", "ease-in-out", "duration-300"),
    enterFrom: tw("opacity-0"),
    enterTo: tw("opacity-100"),
    leave: tw("transition", "ease-in-out", "duration-300"),
    leaveFrom: tw("opacity-100"),
    leaveTo: tw("opacity-0"),
  },
};

const styles = {
  container: tw(
    "fixed",
    "left-0",
    "top-0",
    "z-max",
    "w-full",
    "flex",
    "flex-row",
    "flex-nowrap",
    "justify-center",
    "items-center"
  ),
  backdrop: tw("h-screen", "w-full", "bg-overlay"),
  modal: tw("fixed"),
};

type Props = {
  closeModal: (e: React.MouseEvent<HTMLElement> | KeyboardEvent) => void;
};

const ModalProviderBase = ({
  render,
  targetModal,
}: {
  render: (props: Props) => ReactNode;
  targetModal: PageModal;
}) => {
  const {
    ui: { pageModalTarget },
  } = useSyncExternalStore<TablescapeState>(
    TablescapeContext.onAll,
    TablescapeContext.getSnapshot
  );

  const pageModalOpen = useMemo(
    () => pageModalTarget === targetModal,
    [pageModalTarget, targetModal]
  );

  const closeModal = useCallback(
    (e: React.MouseEvent<HTMLElement> | KeyboardEvent) => {
      e.preventDefault();
      TablescapeContext.emit(TablescapeEvent.TOGGLE_PAGE_MODAL, {
        ui: {
          pageModalTarget: null,
        },
      });
    },
    []
  );

  const memoizedModal = useMemo(
    () => render({ closeModal }),
    [render, closeModal]
  );

  useEffect(() => {
    if (pageModalOpen) {
      const scrollY = window.scrollY;
      document.body.classList.add("overflow-hidden");
      document.body.classList.add("h-dynamic-screen");
      document.body.classList.add("fixed");
      document.body.style.top = `-${scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.classList.remove("overflow-hidden");
      document.body.classList.remove("h-dynamic-screen");
      document.body.classList.remove("fixed");
      if (scrollY) window.scrollTo(0, parseInt(scrollY) * -1);
    }
  }, [pageModalOpen]);

  const handleEscKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeModal(e);
      }
    },
    [closeModal]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleEscKey);

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [handleEscKey]);

  return (
    <Transition className={styles.container} show={pageModalOpen}>
      {/* Backdrop */}
      <Transition.Child
        className={styles.backdrop}
        onClick={closeModal}
        {...transitionAnimation.backdrop}
      />
      {/* Modal */}
      <Transition.Child className={styles.modal} {...transitionAnimation.modal}>
        {memoizedModal}
      </Transition.Child>
    </Transition>
  );
};

export const ModalProvider = memo(ModalProviderBase);
